import type { ReactNode } from "react";
import { ArrowRight, Mail, MessageSquare, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { ConnectionBrandIcon } from "@/components/ConnectionBrandIcon";
import { PublicLayout } from "@/components/PublicLayout";
import { PublicPageHero } from "@/components/PublicPageHero";
import {
  ATW_CONTACT_URL,
  SLACK_MARKETPLACE_APPROVED,
  SUPPORT_EMAIL,
  SUPPORT_HOURS,
  SUPPORT_PHONE,
  SUPPORT_PHONE_HREF,
  SUPPORT_RESPONSE,
} from "@/lib/publicSite";

type Faq = { q: string; a: ReactNode };

const mail = <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>;

const FAQS: Faq[] = [
  {
    q: "Do I need an ATW Hub account?",
    a: (
      <>
        Yes. You sign in to Hub with your ATW account, and your organization's ATW admin turns on Hub for
        you. If your business doesn't use ATW Hub yet, email {mail} to get started.
      </>
    ),
  },
  {
    q: "How do I connect Slack?",
    a: (
      <>
        Go to <Link to="/slack">hub.atw.net/slack</Link> and click Add to Slack, or in Hub open
        Settings, then Connections, then Chat, and click Connect Slack. Pick your workspace and click
        Allow. If your workspace requires approval for new apps, Slack asks an admin first.
      </>
    ),
  },
  {
    q: "How do I disconnect Slack?",
    a: (
      <>
        In Hub, open Settings, then Connections, then Chat, and click Disconnect next to the Slack
        account. Hub revokes its access with Slack and deletes the tokens, read markers, and saved links
        for that account. Your messages in Slack don't change.
      </>
    ),
  },
  {
    q: "How do I uninstall ATW Hub from Slack?",
    a: (
      <>
        In Slack on your computer, click your workspace name, choose Tools &amp; settings, then Manage
        apps. Select Installed apps, open ATW Hub, click App Details, go to the Configuration tab, and
        click Remove App. This removes the app for your whole workspace, and Hub deletes the tokens, read
        markers, and saved links for everyone in it. If your workspace limits who can remove apps, ask an admin. To
        stop using Hub just for yourself, disconnect in Hub instead.
      </>
    ),
  },
  {
    q: "What Slack data does Hub keep?",
    a: (
      <>
        Your encrypted Slack sign-in tokens, basic details about your Slack account, which conversations
        you've read, and the links you save with Send to Hub. Hub doesn't store message text, files, or
        search results, and it doesn't read Slack in the background. The{" "}
        <Link to="/privacy">privacy policy</Link> has the details.
      </>
    ),
  },
  {
    q: "How do I get a copy of my data, or have it deleted?",
    a: (
      <>
        Email {mail} from the address on your Hub account. We reply within 2 business days and complete
        requests within 30 days.
      </>
    ),
  },
  {
    q: "Why don't I see Needs you or Hub AI for Slack?",
    a: SLACK_MARKETPLACE_APPROVED ? (
      <>
        Slack doesn't let workspace guests use these features. If you're a full member and still don't
        see them, email {mail} and tell us your workspace name.
      </>
    ) : (
      <>
        Slack only lets apps listed in the Slack Marketplace use its search and AI tools, and ATW Hub is
        waiting for approval. Both turn on after approval. Slack also doesn't let workspace guests use
        them.
      </>
    ),
  },
  ...(SLACK_MARKETPLACE_APPROVED
    ? []
    : [
        {
          q: "Why are my Slack conversations slow to load?",
          a: (
            <>
              Until ATW Hub is in the Slack Marketplace, Slack lets it load message history only once a
              minute, 15 messages at a time. If a conversation doesn't load, wait a minute and open it
              again.
            </>
          ),
        },
      ]),
];

const GUIDES = [
  { to: "/docs/slack", id: "slack" as const, label: "ATW Hub for Slack" },
  { to: "/docs/zoom", id: "zoom" as const, label: "ATW Hub for Zoom" },
  { to: "/docs/teams", id: "teams" as const, label: "ATW Hub for Microsoft Teams" },
] as const;

export function SupportPage() {
  return (
    <PublicLayout title="ATW Hub Support">
      <article className="public-doc">
        <PublicPageHero
          eyebrow="Help"
          title="ATW Hub support"
          lede={
            <>
              Help with ATW Hub and connected services (Slack, Microsoft Teams, Zoom, Google, and Zoho). You
              don't need an account to contact us.
            </>
          }
        />

        <div className="public-contact-grid">
          <a href={`mailto:${SUPPORT_EMAIL}`} className="public-contact-card">
            <span className="public-contact-card-icon" aria-hidden>
              <Mail size={20} strokeWidth={2} />
            </span>
            <h2>Email</h2>
            <p className="public-contact-value">{SUPPORT_EMAIL}</p>
            <p className="public-note">Best for most questions. {SUPPORT_RESPONSE}</p>
          </a>
          <a href={SUPPORT_PHONE_HREF} className="public-contact-card">
            <span className="public-contact-card-icon" aria-hidden>
              <Phone size={20} strokeWidth={2} />
            </span>
            <h2>Phone</h2>
            <p className="public-contact-value">{SUPPORT_PHONE}</p>
            <p className="public-note">{SUPPORT_HOURS}</p>
          </a>
          <a href={ATW_CONTACT_URL} className="public-contact-card">
            <span className="public-contact-card-icon" aria-hidden>
              <MessageSquare size={20} strokeWidth={2} />
            </span>
            <h2>Contact form</h2>
            <p className="public-contact-value">atw.net/contact</p>
            <p className="public-note">No sign-in needed. {SUPPORT_RESPONSE}</p>
          </a>
        </div>

        <section className="public-doc-section">
          <h2>Integration guides</h2>
          <div className="public-guide-grid">
            {GUIDES.map((guide) => (
              <Link key={guide.to} to={guide.to} className="public-guide-card">
                <ConnectionBrandIcon id={guide.id} size={28} />
                <span className="public-guide-card-label">{guide.label}</span>
                <ArrowRight className="public-guide-card-arrow" size={18} aria-hidden />
              </Link>
            ))}
          </div>
          <p>
            Security overview: <Link to="/security">hub.atw.net/security</Link>. Policies:{" "}
            <Link to="/privacy">Privacy</Link>, <Link to="/terms">Terms</Link>.
          </p>
        </section>

        <section className="public-doc-section">
          <h2>What to include</h2>
          <p>
            Your Slack workspace name, what you were doing, and what you saw. A screenshot helps, but hide
            anything private. Never send passwords or sign-in tokens.
          </p>
          <p className="public-note">
            Business days are Monday to Friday, excluding U.S. public holidays.
          </p>
        </section>

        <section className="public-doc-section">
          <h2>Common questions</h2>
          {FAQS.map((faq) => (
            <details key={faq.q} className="public-accordion">
              <summary>{faq.q}</summary>
              <div className="public-accordion-body">
                <p>{faq.a}</p>
              </div>
            </details>
          ))}
        </section>
      </article>
    </PublicLayout>
  );
}
